<template>
  <div class="user-news-index">
    <Teleport v-if="afetrTitleActionSpaceMounted" to="#page-title-action-space">
      <BasicButton
        label="お知らせ一覧へ"
        class="add-btn"
        variant="secondary"
        outlined
        @click="goto({ name: 'UsersNewsIndex' })"
      />
    </Teleport>

    <UserPageActions>
      <Card class="w-100">
        <template #content>
          <div class="d-flex align-items-center justify-content-between w-100">
            <div class="">
              <p>各種フィルター</p>
              <div class="d-flex align-items-center">
                <div class="d-flex flex-column me-2">
                  <p class="mb-2">組織</p>
                  <Dropdown
                    v-model="selectedOrganizationId"
                    :options="organizations"
                    option-value="id"
                    option-label="name"
                  />
                </div>

                <div class="d-flex flex-column me-2">
                  <p class="mb-2">公開範囲</p>
                  <Dropdown
                    v-model="selectedForMembers"
                    :options="newsFilterOptions"
                    option-value="value"
                    option-label="label"
                  />
                </div>

                <div class="d-flex flex-column">
                  <p class="mb-2">種別</p>
                  <Dropdown
                    v-model="selectedNewsLabel"
                    :options="['all', ...newsLabels]"
                    :option-label="
                      (v) => (v === 'all' ? '全て' : labelTranslater(v))
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </UserPageActions>

    <Card>
      <template #content>
        <span v-if="news && !news.length">ゴミ箱は空です</span>
        <NewsTable
          v-else-if="news"
          :news="filteredNews"
          trashed
          @select-news="selectNews"
          @trash-news="handleDestroyNews"
          @restore-news="handleRestoreNews"
        />
        <i v-else class="pi pi-spin pi-spinner"></i>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, inject } from "vue";
import { getStorage, setStorage } from "/@/modules/localStorage";
import { useRouterUtil, useUser } from "/@/vue/composables";
import { useNews } from "/@/vue/composables";
import { BasicButton } from "/@/vue/components/Atom";
import { Table as NewsTable } from "/@/vue/components/Organisms/Users/News";
import { UserPageActions } from "/@/vue/components/Layouts";
import Dropdown from "primevue/dropdown";
import Card from "primevue/card";
import { NewsClient, newsLabels } from "/@/types";
import { labelTranslater } from "/@/modules/news";

const afetrTitleActionSpaceMounted = inject("afetrTitleActionSpaceMounted");

const { goto } = useRouterUtil();

function selectNews(id: number) {
  goto({ name: "UsersNewsShow", params: { id: id } });
}

// api

const { getUser } = useUser();
const { data: user } = getUser();

const organizations = computed(() => {
  if (!user.value) return [];

  return user.value.organizations;
});

const selectedOrganizationId = ref(
  getStorage("selectedOrganizationId") || undefined
);

watch(selectedOrganizationId, (id) => {
  if (!id) return;
  setStorage("selectedOrganizationId", id);
});

watch(user, (u) => {
  if (!u || !!selectedOrganizationId.value) return;

  selectedOrganizationId.value = u.organizations[0]?.id;
});

const {
  getTrashedNews,
  destroyNews,
  restoreNews,
  selectedNewsLabel,
  selectedForMembers,
  newsFilter,
  newsFilterOptions,
} = useNews();
const { data: news, mutate: getNewsMutate } = getTrashedNews(
  selectedOrganizationId
);

const filteredNews = computed(() => {
  if (!news.value) return [];

  return newsFilter(
    news.value,
    selectedNewsLabel.value,
    selectedForMembers.value
  );
});

async function handleDestroyNews(id: number | number[]) {
  if (!window.confirm("完全に削除しますか？この操作は取り消せません。")) {
    return;
  }

  if (await destroyNews(id)) {
    alert("削除しました");
    getNewsMutate();
  } else {
    alert("削除に失敗しました");
  }
}

async function handleRestoreNews(id: number | number[]) {
  if (!window.confirm("復元しますか？")) {
    return;
  }

  if (await restoreNews(id)) {
    alert("復元しました");
    getNewsMutate();
  } else {
    alert(
      "復元に失敗しました。時間をおいて再度お試しください。解決しない場合は開発会社にお問い合わせください。"
    );
  }
}
</script>

<style lang="scss" scoped></style>
